import React, { useState, useEffect } from 'react'
import { Shield, Eye, EyeOff, AlertTriangle } from 'lucide-react'
import { supabase } from '../services/supabase'
import { TurnstileWidget, isTurnstileEnabled, requireCaptchaToken } from './TurnstileWidget'

function isPlatformAdminRole(role) {
  return role === 'admin' || role === 'super_admin'
}

const AdminLogin = ({ onLoginSuccess }) => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [captchaToken, setCaptchaToken] = useState('')

  // Admin login is always light — don't inherit the family app's dark class
  useEffect(() => {
    document.documentElement.classList.remove('dark')
  }, [])

  const handleLogin = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      requireCaptchaToken(captchaToken)

      const { data: { user }, error: signInError } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
        ...(captchaToken ? { options: { captchaToken } } : {}),
      })

      if (signInError) throw signInError
      if (!user) throw new Error('Invalid credentials')

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', user.id)
        .single()

      if (profileError) {
        if (profileError.code === 'PGRST116') {
          await supabase.auth.signOut()
          throw new Error('ADMIN_SETUP_INCOMPLETE: no profile row for this user (run promote-admin SQL)')
        }
        throw profileError
      }

      if (!isPlatformAdminRole(profile?.role)) {
        await supabase.auth.signOut()
        throw new Error('ACCESS_DENIED: user lacks admin/super_admin role')
      }

      let adminTier = profile.role === 'super_admin' ? 'super_admin' : 'admin'
      try {
        const { data: adminRow } = await supabase
          .from('admin_users')
          .select('role')
          .eq('email', user.email)
          .maybeSingle()
        if (adminRow?.role === 'super_admin') adminTier = 'super_admin'
      } catch (_) {
        // admin_users is optional; profiles.role is enough to enter
      }

      localStorage.setItem('adminSession', 'true')
      localStorage.setItem('adminUser', JSON.stringify(user))
      localStorage.setItem('adminTier', adminTier)
      onLoginSuccess(user)
    } catch (err) {
      // Keep the detailed reason in the console for the developer only — the
      // on-screen message stays generic so the public admin page never leaks
      // the backend stack (Supabase), setup steps, or whether an account exists.
      console.error('Admin login failed:', err?.code || '', err?.message || err)
      setCaptchaToken('')
      setError('Unable to sign in. Please check your credentials and try again.')
    } finally {
      setLoading(false)
    }
  }

  const inputClassName =
    'block w-full rounded-lg border-2 border-slate-300 bg-slate-50 px-3 py-2.5 text-slate-900 placeholder:text-slate-400 shadow-sm outline-none transition focus:border-blue-600 focus:bg-white focus:ring-2 focus:ring-blue-500/30 sm:text-sm'

  return (
    <div className="admin-login-page min-h-screen bg-slate-200 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <div className="rounded-2xl bg-blue-600 p-3 shadow-lg">
            <Shield className="h-10 w-10 text-white" aria-hidden="true" />
          </div>
        </div>
        <h1 className="mt-6 text-center text-3xl font-extrabold tracking-tight text-slate-900">
          Admin Access
        </h1>
        <p className="mt-2 text-center text-sm text-slate-600">
          Sign in to manage your TrophyTime platform
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="rounded-2xl border border-slate-300 bg-white py-8 px-6 shadow-xl sm:px-10">
          <form className="space-y-6" onSubmit={handleLogin}>
            {error && (
              <div className="rounded-lg border border-red-300 bg-red-50 p-4" role="alert">
                <div className="flex gap-3">
                  <AlertTriangle className="h-5 w-5 shrink-0 text-red-600" aria-hidden="true" />
                  <p className="text-sm font-medium text-red-800">{error}</p>
                </div>
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-slate-800">
                Admin Email
              </label>
              <div className="mt-1.5">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={inputClassName}
                  placeholder="aabshir@outlook.com"
                  aria-label="Admin email"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-semibold text-slate-800">
                Admin Password
              </label>
              <div className="mt-1.5 relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`${inputClassName} pr-11`}
                  placeholder="Enter password"
                  aria-label="Admin password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-500 hover:text-slate-800"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            {isTurnstileEnabled() && (
              <TurnstileWidget onToken={setCaptchaToken} />
            )}

            <button
              type="submit"
              disabled={loading || (isTurnstileEnabled() && !captchaToken)}
              className="flex w-full items-center justify-center rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Signing in...
                </span>
              ) : (
                'Sign in to Admin Dashboard'
              )}
            </button>
          </form>

          <div className="mt-6 border-t border-slate-200 pt-5 text-center">
            <p className="text-xs leading-relaxed text-slate-500">
              Restricted to authorized administrators only.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdminLogin
